import React, { useState, useEffect } from 'react';
import {
    Table,
    TableBody,
    TableCell,
    TableRow,
    Typography,
    TextField,
    InputAdornment,
    IconButton,
} from '@mui/material';
import formatPrice from '../../../../../utils/formatPrice';
import SwitchLeftIcon from '@mui/icons-material/SwitchLeft';
import SwitchRightIcon from '@mui/icons-material/SwitchRight';

const PriceTable = ({ orders, dispatchOrder, defaultTax }) => {
    const initialDiscount =
        orders.discount.discount_type === 'Dollar'
            ? orders.discount.amount_off || 0 / 100
            : orders.discount.percent_off || 0;
    const [inputDiscount, setInputDiscount] = useState(initialDiscount);
    const [inputTax, setInputTax] = useState(
        orders ? orders.payment_salestax_percent : defaultTax
    );

    // Initialize gratuity object if not present
    if (!orders.gratuity) {
        orders.gratuity = { gratuity_type: 'Dollar', amount: 0, percent: 0 };
    }
    if (!orders.deliveryFee) {
        orders.deliveryFee = {
            deliveryFee_type: 'Dollar',
            amount: 0,
            percent: 0,
        };
    }

    // Initialize gratuity state
    const initialGratuity = orders.gratuity
        ? orders.gratuity.gratuity_type === 'Dollar'
            ? orders.gratuity.amount / 100
            : orders.gratuity.percent
        : 0;
    const [inputGratuity, setInputGratuity] = useState(initialGratuity);

    // Initialize delivery fee state
    const initialDeliveryFee = orders.deliveryFee
        ? orders.deliveryFee.deliveryFee_type === 'Dollar'
            ? orders.deliveryFee.amount / 100
            : orders.deliveryFee.percent
        : 0;
    const [inputDeliveryFee, setInputDeliveryFee] =
        useState(initialDeliveryFee);
    useEffect(() => {
        if (!inputTax) {
            setInputTax(orders ? orders.payment_salestax_percent : defaultTax);
        }
    }, [defaultTax]);

    // Helper function to calculate total including all fees
    const calculateTotal = (orderData) => {
        const subtotal = orderData.payment_subtotal || 0;
        const tax = orderData.payment_salestax || 0;
        const gratuityAmount = orderData.gratuity?.amount || 0;
        const deliveryFeeAmount = orderData.deliveryFee?.amount || 0;

        return subtotal + tax + gratuityAmount + deliveryFeeAmount;
    };

    const updateDiscount = (value) => {
        //debugger;
        setInputDiscount(value);
        let input = value;
        if (value === '') input = 0;

        if (value !== '') {
            input = Number(value);
            if (isNaN(input)) return;
        }
        var newOrders;
        if (orders.discount.discount_type === 'Dollar') {
            newOrders = {
                ...orders,
                discount: {
                    discount_type: 'Dollar',
                    amount: input * 100,
                    amount_off: input * 100,
                },
                payment_subtotal:
                    orders.payment_subtotal_before_discount - input * 100,
                payment_salestax: Math.round(
                    (orders.payment_salestax_percent / 100) *
                        Math.round(
                            orders.payment_subtotal_before_discount -
                                input * 100
                        )
                ),
            };
        } else {
            newOrders = {
                ...orders,
                discount: {
                    discount_type: 'Percent',
                    amount:
                        (orders.payment_subtotal_before_discount * input) / 100,
                    percent_off: input,
                },
                payment_subtotal: Math.round(
                    orders.payment_subtotal_before_discount * (1 - input / 100)
                ),
                payment_salestax: Math.round(
                    (orders.payment_salestax_percent / 100) *
                        Math.round(
                            orders.payment_subtotal_before_discount *
                                (1 - input / 100)
                        )
                ),
            };
        }
        // Recalculate total with all fees
        newOrders.payment_total = calculateTotal(newOrders);
        dispatchOrder({ type: 'UPDATE_ALL', newOrders });
    };

    const toggleDiscountMethod = () => {
        let currDiscount = inputDiscount;
        if (inputDiscount === '') currDiscount = 0;
        if (inputDiscount !== '') {
            currDiscount = Number(inputDiscount);
            if (isNaN(currDiscount)) currDiscount = 0;
        }
        var newOrders;
        if (orders.discount.discount_type === 'Dollar') {
            newOrders = {
                ...orders,
                discount: {
                    discount_type: 'Percent',
                    percent_off: currDiscount,
                    amount:
                        (orders.payment_subtotal_before_discount *
                            currDiscount) /
                        100,
                },
                payment_subtotal: Math.round(
                    orders.payment_subtotal_before_discount *
                        (1 - currDiscount / 100)
                ),
                payment_salestax: Math.round(
                    (orders.payment_salestax_percent / 100) *
                        Math.round(
                            orders.payment_subtotal_before_discount *
                                (1 - currDiscount / 100)
                        )
                ),
            };
        } else {
            newOrders = {
                ...orders,
                discount: {
                    discount_type: 'Dollar',
                    amount_off: currDiscount * 100,
                    amount: currDiscount * 100,
                },
                payment_subtotal: Math.round(
                    orders.payment_subtotal_before_discount - currDiscount * 100
                ),
                payment_salestax: Math.round(
                    (orders.payment_salestax_percent / 100) *
                        Math.round(
                            orders.payment_subtotal_before_discount -
                                currDiscount * 100
                        )
                ),
            };
        }
        // Recalculate total with all fees
        newOrders.payment_total = calculateTotal(newOrders);
        dispatchOrder({ type: 'UPDATE_ALL', newOrders });
    };

    const updateTax = (value) => {
        setInputTax(value);
        let input = value;
        if (value === '') input = 0;

        if (value !== '') {
            input = Number(value);
            if (isNaN(input)) return;
        }

        var newOrders = {
            ...orders,
            payment_salestax_percent: input,
            payment_salestax: Math.round(
                (input / 100) * orders.payment_subtotal
            ),
        };
        // Recalculate total with gratuity and delivery fee
        newOrders.payment_total = calculateTotal(newOrders);
        dispatchOrder({ type: 'UPDATE_ALL', newOrders });
    };

    const updateGratuity = (value) => {
        setInputGratuity(value);
        let input = value;
        if (value === '') input = 0;

        if (value !== '') {
            input = Number(value);
            if (isNaN(input)) return;
        }

        var newOrders = { ...orders };
        if (!newOrders.gratuity) {
            newOrders.gratuity = {
                gratuity_type: 'Dollar',
                amount: 0,
                percent: 0,
            };
        }

        if (newOrders.gratuity.gratuity_type === 'Dollar') {
            newOrders.gratuity.amount = input * 100;
            newOrders.gratuity.percent = 0;
        } else {
            newOrders.gratuity.percent = input;
            newOrders.gratuity.amount = Math.round(
                (newOrders.payment_subtotal * input) / 100
            );
        }

        // Recalculate total with gratuity
        newOrders.payment_total = calculateTotal(newOrders);
        dispatchOrder({ type: 'UPDATE_ALL', newOrders });
    };

    const toggleGratuityMethod = () => {
        let currGratuity = inputGratuity;
        if (inputGratuity === '') currGratuity = 0;
        if (inputGratuity !== '') {
            currGratuity = Number(inputGratuity);
            if (isNaN(currGratuity)) currGratuity = 0;
        }

        var newOrders = { ...orders };
        if (!newOrders.gratuity) {
            newOrders.gratuity = {
                gratuity_type: 'Dollar',
                amount: 0,
                percent: 0,
            };
        }

        if (newOrders.gratuity.gratuity_type === 'Dollar') {
            // Switching from Dollar to Percent
            newOrders.gratuity = {
                gratuity_type: 'Percent',
                percent: currGratuity,
                amount: Math.round(
                    (newOrders.payment_subtotal * currGratuity) / 100
                ),
            };
        } else {
            // Switching from Percent to Dollar
            newOrders.gratuity = {
                gratuity_type: 'Dollar',
                amount: currGratuity * 100,
                percent: 0,
            };
        }

        // Recalculate total
        newOrders.payment_total = calculateTotal(newOrders);
        dispatchOrder({ type: 'UPDATE_ALL', newOrders });
    };

    const updateDeliveryFee = (value) => {
        setInputDeliveryFee(value);
        let input = value;
        if (value === '') input = 0;

        if (value !== '') {
            input = Number(value);
            if (isNaN(input)) return;
        }

        var newOrders = { ...orders };
        if (!newOrders.deliveryFee) {
            newOrders.deliveryFee = {
                deliveryFee_type: 'Dollar',
                amount: 0,
                percent: 0,
            };
        }

        if (newOrders.deliveryFee.deliveryFee_type === 'Dollar') {
            newOrders.deliveryFee.amount = input * 100;
            newOrders.deliveryFee.percent = 0;
        } else {
            newOrders.deliveryFee.percent = input;
            newOrders.deliveryFee.amount = Math.round(
                (newOrders.payment_subtotal * input) / 100
            );
        }

        // Recalculate total with delivery fee
        newOrders.payment_total = calculateTotal(newOrders);
        dispatchOrder({ type: 'UPDATE_ALL', newOrders });
    };

    const toggleDeliveryFeeMethod = () => {
        let currDeliveryFee = inputDeliveryFee;
        if (inputDeliveryFee === '') currDeliveryFee = 0;
        if (inputDeliveryFee !== '') {
            currDeliveryFee = Number(inputDeliveryFee);
            if (isNaN(currDeliveryFee)) currDeliveryFee = 0;
        }

        var newOrders = { ...orders };
        if (!newOrders.deliveryFee) {
            newOrders.deliveryFee = {
                deliveryFee_type: 'Dollar',
                amount: 0,
                percent: 0,
            };
        }

        if (newOrders.deliveryFee.deliveryFee_type === 'Dollar') {
            // Switching from Dollar to Percent
            newOrders.deliveryFee = {
                deliveryFee_type: 'Percent',
                percent: currDeliveryFee,
                amount: Math.round(
                    (newOrders.payment_subtotal * currDeliveryFee) / 100
                ),
            };
        } else {
            // Switching from Percent to Dollar
            newOrders.deliveryFee = {
                deliveryFee_type: 'Dollar',
                amount: currDeliveryFee * 100,
                percent: 0,
            };
        }

        // Recalculate total
        newOrders.payment_total = calculateTotal(newOrders);
        dispatchOrder({ type: 'UPDATE_ALL', newOrders });
    };

    return (
        <Table size="small" sx={{ mb: 1 }}>
            <TableBody>
                <TableRow>
                    <TableCell variant="head" align="left">
                        Subtotal
                    </TableCell>
                    <TableCell align="left">
                        <Typography variant="body2">
                            {`Original Subtotal: ${formatPrice(
                                orders.payment_subtotal_original / 100
                            )}`}
                        </Typography>
                    </TableCell>
                    <TableCell align="left">
                        <Typography variant="body2">
                            {`Actual Subtotal: ${formatPrice(
                                orders.payment_subtotal_before_discount / 100
                            )}`}
                        </Typography>
                    </TableCell>
                </TableRow>

                <TableRow>
                    <TableCell variant="head" align="left">
                        Additional Discount
                    </TableCell>
                    <TableCell align="left">
                        <TextField
                            size="small"
                            value={inputDiscount}
                            onChange={(e) => updateDiscount(e.target.value)}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <IconButton
                                            size="small"
                                            sx={{ m: 0, p: 0 }}
                                            onClick={toggleDiscountMethod}
                                        >
                                            {orders.discount.discount_type ===
                                            'Dollar' ? (
                                                <SwitchLeftIcon
                                                    sx={{ fontSize: 15 }}
                                                />
                                            ) : (
                                                <SwitchRightIcon
                                                    sx={{ fontSize: 15 }}
                                                />
                                            )}
                                        </IconButton>
                                        <div style={{ width: 5 }}>
                                            {orders.discount.discount_type ===
                                            'Dollar'
                                                ? '$'
                                                : '%'}
                                        </div>
                                    </InputAdornment>
                                ),
                                style: { height: '30px', fontSize: '.875rem' },
                            }}
                        />
                    </TableCell>
                    <TableCell align="left">
                        <Typography variant="body2">
                            {`Additional Discount Amount: 
                                ${
                                    orders.discount.discount_type === 'Dollar'
                                        ? formatPrice(
                                              (orders.discount.amount || 0) /
                                                  100
                                          )
                                        : formatPrice(
                                              (((orders.discount.percent_off ||
                                                  0) /
                                                  100) *
                                                  orders.payment_subtotal_before_discount) /
                                                  100
                                          )
                                }`}
                        </Typography>
                    </TableCell>
                </TableRow>

                <TableRow>
                    <TableCell variant="head" align="left">
                        Sales Tax
                    </TableCell>
                    <TableCell align="left">
                        <TextField
                            size="small"
                            value={inputTax}
                            onChange={(e) => updateTax(e.target.value)}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        %
                                    </InputAdornment>
                                ),
                                style: { height: '30px', fontSize: '.875rem' },
                            }}
                        />
                    </TableCell>
                    <TableCell variant="head" align="left">
                        <Typography variant="body2">
                            {`Sales Tax Total: ${formatPrice(
                                orders.payment_salestax / 100
                            )}`}
                        </Typography>
                    </TableCell>
                </TableRow>

                <TableRow>
                    <TableCell variant="head" align="left">
                        Gratuity / OtherFee
                    </TableCell>
                    <TableCell align="left">
                        <TextField
                            size="small"
                            value={inputGratuity}
                            onChange={(e) => updateGratuity(e.target.value)}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <IconButton
                                            size="small"
                                            sx={{ m: 0, p: 0 }}
                                            onClick={toggleGratuityMethod}
                                        >
                                            {orders.gratuity?.gratuity_type ===
                                            'Dollar' ? (
                                                <SwitchLeftIcon
                                                    sx={{ fontSize: 15 }}
                                                />
                                            ) : (
                                                <SwitchRightIcon
                                                    sx={{ fontSize: 15 }}
                                                />
                                            )}
                                        </IconButton>
                                        <div style={{ width: 5 }}>
                                            {orders.gratuity?.gratuity_type ===
                                            'Dollar'
                                                ? '$'
                                                : '%'}
                                        </div>
                                    </InputAdornment>
                                ),
                                style: { height: '30px', fontSize: '.875rem' },
                            }}
                        />
                    </TableCell>
                    <TableCell align="left">
                        <Typography variant="body2">
                            {`Gratuity Amount: ${formatPrice(
                                (orders.gratuity?.amount || 0) / 100
                            )}`}
                        </Typography>
                    </TableCell>
                </TableRow>

                <TableRow>
                    <TableCell variant="head" align="left">
                        Delivery Fee
                    </TableCell>
                    <TableCell align="left">
                        <TextField
                            size="small"
                            value={inputDeliveryFee}
                            onChange={(e) => updateDeliveryFee(e.target.value)}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <IconButton
                                            size="small"
                                            sx={{ m: 0, p: 0 }}
                                            onClick={toggleDeliveryFeeMethod}
                                        >
                                            {orders.deliveryFee
                                                ?.deliveryFee_type ===
                                            'Dollar' ? (
                                                <SwitchLeftIcon
                                                    sx={{ fontSize: 15 }}
                                                />
                                            ) : (
                                                <SwitchRightIcon
                                                    sx={{ fontSize: 15 }}
                                                />
                                            )}
                                        </IconButton>
                                        <div style={{ width: 5 }}>
                                            {orders.deliveryFee
                                                ?.deliveryFee_type === 'Dollar'
                                                ? '$'
                                                : '%'}
                                        </div>
                                    </InputAdornment>
                                ),
                                style: { height: '30px', fontSize: '.875rem' },
                            }}
                        />
                    </TableCell>
                    <TableCell align="left">
                        <Typography variant="body2">
                            {`Delivery Fee Amount: ${formatPrice(
                                (orders.deliveryFee?.amount || 0) / 100
                            )}`}
                        </Typography>
                    </TableCell>
                </TableRow>

                <TableCell variant="head" align="left">
                    Final Total
                </TableCell>
                <TableCell variant="head" align="left"></TableCell>
                <TableCell variant="head" align="left">
                    {formatPrice(orders.payment_total / 100)}
                </TableCell>
            </TableBody>
        </Table>
    );
};

export default PriceTable;
